import * as React from "react";

import { cn } from "@/lib/utils";

interface NumberFieldElementProps {
  value: number | string;
  onChange: (value: number | string) => void;
  onBlur?: (value: number | string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
}

const NumberFieldElement = React.forwardRef<HTMLInputElement, NumberFieldElementProps>(
  ({ 
    value, 
    onChange, 
    onBlur,
    placeholder = "Enter number...", 
    min,
    max,
    step = 0.01,
    disabled = false,
    className,
    ...props 
  }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [localValue, setLocalValue] = React.useState<string>('');
    
    // Update local value when external value changes and input is not focused
    React.useEffect(() => {
      if (!isFocused) {
        setLocalValue(value === '' || value === null || value === undefined ? '' : String(value));
      }
    }, [value, isFocused]);
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      // Update local state immediately for smooth typing
      setLocalValue(inputValue);
    };

    const handleFocus = () => {
      setIsFocused(true);
      setLocalValue(value === '' || value === null || value === undefined ? '' : String(value));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      const inputValue = e.target.value;
      
      // Convert to number and call onChange with the final value
      if (inputValue === '' || inputValue === null || inputValue === undefined) {
        onChange('');
        if (onBlur) {
          onBlur('');
        }
      } else {
        const numValue = parseFloat(inputValue);
        const finalValue = !isNaN(numValue) ? numValue : inputValue;
        onChange(finalValue);
        if (onBlur) {
          onBlur(finalValue);
        }
      }
    };

    return (
      <input
        type="number"
        className={cn(
          "flex h-10 w-full border border-input bg-background text-foreground px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        placeholder={placeholder}
        value={isFocused ? localValue : (value === '' || value === null || value === undefined ? '' : value)}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        ref={ref}
        {...props}
      />
    );
  }
);

NumberFieldElement.displayName = "NumberFieldElement";

export { NumberFieldElement };
